// CorsiBoard — catalogo corsi LIVE da 7manager.ch
// - Selettore sedi auto-popolato dall'API
// - Paginazione 6+6 ("Mostra altri 6")
// - Multi-lezione (ogni data + orario)
// - Booking URL con ?back={url_corrente}
// - Fallback automatico a backend proxy se CORS è bloccato (es. preview)
import { useEffect, useMemo, useState } from "react";
import { ArrowRight, Calendar, MapPin, AlertCircle, ArrowDown } from "lucide-react";

const DIRECT_API = "https://7manager.ch/api/public";
const PROXY_API = `${process.env.REACT_APP_BACKEND_URL}/api/catalog`;
const PAGE_SIZE = 6;

// Tenta DIRECT_API per primo; in caso di errore (CORS / 5xx) cade sul proxy backend.
async function fetchWithFallback(subPath) {
    try {
        const r = await fetch(`${DIRECT_API}${subPath}`);
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return await r.json();
    } catch (_directErr) {
        const r2 = await fetch(`${PROXY_API}${subPath}`);
        if (!r2.ok) throw new Error(`HTTP ${r2.status}`);
        return await r2.json();
    }
}

/**
 * <CorsiBoard org="duegi" />                                  // singola org
 * <CorsiBoard orgs={["duegi", "7med"]} />                     // multi-org (corsi uniti)
 * <CorsiBoard org="7med" sede="lugano" hideSedePicker />
 * <CorsiBoard org="7med" tipiIds={["abc","def"]} />
 * <CorsiBoard orgs={["duegi","7med"]} tipoNomeIncludes="samaritan" />   // filtro per nome tipo corso
 */
export default function CorsiBoard({
    org,
    orgs,
    sede: sedeInitial = null,
    tipiIds = [],
    tipoNomeIncludes = null,
    hideSedePicker = false,
    onSelect = null,
}) {
    // Normalizza: accetta sia `org="..."` (string) sia `orgs={["..","..]}` (array)
    const orgList = useMemo(() => {
        if (Array.isArray(orgs) && orgs.length > 0) return orgs;
        if (org) return [org];
        return [];
    }, [org, orgs]);

    const [allCourses, setAllCourses] = useState([]);
    const [sedi, setSedi] = useState([]);
    const [activeSede, setActiveSede] = useState(sedeInitial || "all");
    const [visible, setVisible] = useState(PAGE_SIZE);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // 1) Sedi (per il selettore) — fetch da TUTTE le org e dedupe per comune
    useEffect(() => {
        if (hideSedePicker || orgList.length === 0) return;
        Promise.all(
            orgList.map((slug) =>
                fetchWithFallback(`/org/${slug}`)
                    .then((data) => (data.sedi || []).map((s) => ({ ...s, _orgSlug: slug })))
                    .catch(() => [])
            )
        ).then((arrays) => {
            const flat = arrays.flat();
            // Dedupe per comune (case-insensitive)
            const seen = new Map();
            for (const s of flat) {
                const key = (s.comune || s.nome_pubblico || s.nome_sede || s.slug || "").toLowerCase();
                if (key && !seen.has(key)) seen.set(key, s);
            }
            setSedi(Array.from(seen.values()));
        });
    }, [orgList.join(","), hideSedePicker]); // eslint-disable-line react-hooks/exhaustive-deps

    // 2) Corsi — fetch da TUTTE le org in parallelo, poi merge + sort
    useEffect(() => {
        if (orgList.length === 0) return;
        setLoading(true);
        setError(null);
        setVisible(PAGE_SIZE);
        const qs = tipiIds.length ? `?tipi=${tipiIds.join(",")}` : "";

        // Single-org + sede esplicita → usa endpoint dedicato (più efficiente, no filtering lato client)
        const useSedeEndpoint = orgList.length === 1 && activeSede && activeSede !== "all";

        Promise.all(
            orgList.map((slug) => {
                const path = useSedeEndpoint
                    ? `/org/${slug}/sede/${activeSede}/courses${qs}`
                    : `/org/${slug}/courses${qs}`;
                return fetchWithFallback(path)
                    .then((data) => (data || []).map((c) => ({ ...c, _orgSlug: slug })))
                    .catch(() => []);
            })
        )
            .then((arrays) => {
                const today = new Date().toISOString().slice(0, 10);
                let merged = arrays.flat()
                    .filter((c) => c.data_inizio >= today && c.stato !== "annullato");

                if (tipoNomeIncludes) {
                    const needle = tipoNomeIncludes.toLowerCase();
                    merged = merged.filter((c) => (c.tipo_corso_nome || "").toLowerCase().includes(needle));
                }

                // Filtro lato client per sede attiva (solo se NON abbiamo già fatto endpoint-level filtering)
                if (!useSedeEndpoint && activeSede && activeSede !== "all") {
                    const targetSede = sedi.find((s) => s.slug === activeSede);
                    const targetComune = (targetSede?.comune || "").toLowerCase();
                    merged = merged.filter((c) => {
                        if (c.sede_slug === activeSede) return true;
                        if (targetComune && (c.sede_comune || "").toLowerCase() === targetComune) return true;
                        return false;
                    });
                }

                merged.sort((a, b) => (a.data_inizio || "").localeCompare(b.data_inizio || ""));
                setAllCourses(merged);
            })
            .catch((e) => setError(String(e?.message || e)))
            .finally(() => setLoading(false));
    }, [orgList.join(","), activeSede, tipiIds.join(","), tipoNomeIncludes, sedi]); // eslint-disable-line react-hooks/exhaustive-deps

    const courses = useMemo(() => allCourses.slice(0, visible), [allCourses, visible]);
    const hasMore = visible < allCourses.length;
    const remaining = allCourses.length - visible;

    return (
        <div data-testid="corsi-board">
            {/* Selettore sedi */}
            {!hideSedePicker && sedi.length > 1 && (
                <div className="flex flex-wrap gap-2 mb-8" data-testid="sede-picker">
                    <SedeChip active={activeSede === "all"} onClick={() => setActiveSede("all")} testId="chip-all">
                        Tutte le sedi
                    </SedeChip>
                    {sedi.map((s) => (
                        <SedeChip
                            key={s.slug || s.id}
                            active={activeSede === s.slug}
                            onClick={() => setActiveSede(s.slug)}
                            testId={`chip-${s.slug}`}
                        >
                            {s.nome_pubblico || s.comune || s.nome_sede}
                        </SedeChip>
                    ))}
                </div>
            )}

            {/* Stati */}
            {loading ? <LoadingGrid /> : null}

            {!loading && error ? (
                <div className="bg-white border border-ink/10 p-8 flex items-start gap-4" data-testid="corsi-board-error">
                    <AlertCircle className="w-6 h-6 text-brand flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                    <div>
                        <div className="font-heading font-bold text-lg">Catalogo momentaneamente non disponibile</div>
                        <p className="text-sm text-ink-muted mt-1 leading-relaxed">
                            Non riusciamo a caricare le date dei prossimi corsi. Riprova fra qualche minuto oppure scrivici a{" "}
                            <a href="mailto:backoffice@7med.ch" className="text-brand underline">backoffice@7med.ch</a>.
                        </p>
                    </div>
                </div>
            ) : null}

            {!loading && !error && courses.length === 0 ? (
                <div className="bg-white border border-ink/10 p-8" data-testid="corsi-board-empty">
                    <div className="label-eyebrow text-brand">Nessuna data disponibile</div>
                    <h3 className="mt-3 font-heading font-bold text-2xl tracking-tight">
                        Nuove date in programmazione{activeSede !== "all" ? " per questa sede" : ""}.
                    </h3>
                    <p className="mt-3 text-ink-muted leading-relaxed">
                        Non ci sono al momento corsi pubblicati. Lasciaci una richiesta e ti contattiamo non appena
                        apriamo le iscrizioni alla prossima sessione.
                    </p>
                    <a href="mailto:backoffice@7med.ch" className="btn-secondary mt-6 inline-flex">Contattaci</a>
                </div>
            ) : null}

            {/* Griglia */}
            {!loading && !error && courses.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-10 lg:gap-12">
                    {courses.map((c) => (
                        <CorsoCard key={c.id} corso={c} orgSlug={c._orgSlug || orgList[0]} onSelect={onSelect} />
                    ))}
                </div>
            ) : null}

            {/* Mostra altri */}
            {hasMore ? (
                <div className="mt-12 flex flex-col items-center gap-3">
                    <button
                        onClick={() => setVisible((v) => v + PAGE_SIZE)}
                        className="inline-flex items-center gap-2 px-7 py-3.5 border-2 border-brand text-brand hover:bg-brand hover:text-white font-bold text-sm tracking-tight transition-colors"
                        data-testid="corsi-board-show-more"
                    >
                        Mostra altri {Math.min(PAGE_SIZE, remaining)} corsi <ArrowDown className="w-4 h-4" />
                    </button>
                </div>
            ) : null}

            {/* Counter */}
            {!loading && allCourses.length > 0 ? (
                <div className="mt-4 text-center text-[11px] font-mono uppercase tracking-[0.18em] text-ink-muted">
                    {Math.min(visible, allCourses.length)} di {allCourses.length} corsi
                </div>
            ) : null}
        </div>
    );
}

function SedeChip({ active, onClick, children, testId }) {
    return (
        <button
            onClick={onClick}
            type="button"
            className={`px-4 py-2 text-sm font-bold tracking-tight transition-colors border ${
                active
                    ? "bg-brand text-white border-brand"
                    : "bg-white text-ink border-ink/15 hover:border-ink"
            }`}
            data-testid={testId}
        >
            {children}
        </button>
    );
}

function LoadingGrid() {
    return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-10 lg:gap-12" data-testid="corsi-board-loading">
            {[0, 1, 2].map((i) => (
                <div key={i} className="bg-white border border-ink/10 p-7 lg:p-8 animate-pulse">
                    <div className="h-3 bg-ink/10 w-24 mb-6" />
                    <div className="h-10 bg-ink/10 w-3/4 mb-3" />
                    <div className="h-4 bg-ink/10 w-1/2 mb-6" />
                    <div className="h-4 bg-ink/10 w-2/3 mb-2" />
                    <div className="h-12 bg-ink/10 w-full mt-6" />
                </div>
            ))}
        </div>
    );
}

function CorsoCard({ corso, orgSlug, onSelect }) {
    const isFull = corso.corso_pieno;
    const giorni = corso.durata_giorni || 1;
    const giorniLabel = giorni === 1 ? "1 giornata" : `${giorni} giornate`;
    const sedeTitle = corso.sede_nome_pubblico || corso.sede_comune || corso.sede_nome || "—";
    const lezioniValid = (corso.lezioni || []).filter((l) => l.data_inizio && l.data_fine);

    const backUrl = typeof window !== "undefined" ? encodeURIComponent(window.location.href) : "";
    const bookingUrl = `https://7manager.ch/o/${orgSlug}/booking/${corso.id}${backUrl ? `?back=${backUrl}` : ""}`;

    const handleClick = (e) => {
        if (isFull) return;
        if (onSelect) {
            e.preventDefault();
            onSelect(corso);
        }
    };

    // Pillola posti: 3 stati (full / ultimi / disponibili)
    let labelClass = "bg-emerald-50 text-emerald-800 border-emerald-200";
    if (isFull) labelClass = "bg-rose-50 text-rose-700 border-rose-200";
    else if (corso.posti_disponibili != null && corso.posti_disponibili <= 5) {
        labelClass = "bg-amber-50 text-amber-800 border-amber-200";
    }

    return (
        <article
            className={`bg-white border border-ink/10 p-7 lg:p-8 flex flex-col transition-all ${
                isFull ? "opacity-70" : "hover:border-ink hover:shadow-[0_8px_30px_rgba(0,0,0,0.06)] hover:-translate-y-0.5"
            }`}
            data-testid={`corso-card-${corso.id}`}
        >
            {/* Header: giornate + pillola posti */}
            <div className="flex items-center justify-between gap-3 mb-5">
                <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-brand font-bold">
                    {giorniLabel}
                </span>
                {corso.posti_label ? (
                    <span className={`text-[11px] font-semibold px-2.5 py-1 border ${labelClass}`}>
                        {corso.posti_label}
                    </span>
                ) : null}
            </div>

            {/* Sede pubblica — titolone */}
            <h3 className="font-heading font-black text-3xl lg:text-4xl tracking-tighter leading-[1.05] text-ink">
                {sedeTitle}
            </h3>

            {corso.tipo_corso_nome ? (
                <div className="mt-1.5 text-sm text-ink-muted">{corso.tipo_corso_nome}</div>
            ) : null}

            {/* Lezioni multiple */}
            <div className="mt-6 space-y-2 text-[15px] text-ink">
                {lezioniValid.length > 0 ? (
                    lezioniValid.map((lz) => (
                        <div key={lz.data_inizio} className="flex items-start gap-3">
                            <Calendar className="w-4 h-4 text-brand flex-shrink-0 mt-1" strokeWidth={1.8} />
                            <div>
                                <span className="font-semibold">{fmtDate(lz.data_inizio.slice(0, 10))}</span>
                                <span className="text-ink-muted"> · {fmtTime(lz.data_inizio)}–{fmtTime(lz.data_fine)}</span>
                            </div>
                        </div>
                    ))
                ) : corso.data_inizio ? (
                    <div className="flex items-start gap-3">
                        <Calendar className="w-4 h-4 text-brand flex-shrink-0 mt-1" strokeWidth={1.8} />
                        <span className="font-semibold">{formatDateRange(corso.data_inizio, corso.data_fine)}</span>
                    </div>
                ) : null}

                {corso.sede_indirizzo ? (
                    <div className="flex items-start gap-3 text-ink-muted">
                        <MapPin className="w-4 h-4 text-brand flex-shrink-0 mt-1" strokeWidth={1.8} />
                        <span className="leading-snug">{corso.sede_indirizzo}</span>
                    </div>
                ) : null}
            </div>

            {/* Prezzo */}
            {corso.prezzo > 0 ? (
                <div className="mt-6 pt-5 border-t border-ink/10">
                    <div className="font-heading font-black text-3xl tracking-tighter text-brand">
                        CHF {Number(corso.prezzo).toFixed(2)}
                    </div>
                    <div className="text-xs text-ink-muted mt-1">Include certificato di partecipazione</div>
                </div>
            ) : null}

            {/* CTA in fondo */}
            <div className="mt-6 flex-1 flex items-end">
                {isFull ? (
                    <button
                        disabled
                        className="w-full py-3.5 bg-ink/5 text-ink-muted text-sm font-bold tracking-tight cursor-not-allowed"
                        data-testid={`corso-full-${corso.id}`}
                    >
                        Corso completo
                    </button>
                ) : (
                    <a
                        href={bookingUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={handleClick}
                        className="w-full inline-flex items-center justify-center gap-2 py-3.5 bg-brand text-white text-sm font-bold tracking-tight hover:bg-ink transition-colors"
                        data-testid={`corso-book-${corso.id}`}
                    >
                        Iscriviti <ArrowRight className="w-4 h-4" />
                    </a>
                )}
            </div>
        </article>
    );
}

function fmtDate(s) {
    if (!s) return "";
    const [y, m, d] = s.split("-");
    return `${d}/${m}/${y}`;
}

function fmtTime(iso) {
    try {
        return new Date(iso).toLocaleTimeString("it-CH", { hour: "2-digit", minute: "2-digit" });
    } catch {
        return "";
    }
}

function formatDateRange(start, end) {
    if (!start) return "";
    if (!end || end === start) return fmtDate(start);
    return `${fmtDate(start)} – ${fmtDate(end)}`;
}
